import * as React from 'react';
import { styled, useTheme } from '../theme-provider';
import { GalleryContext } from '.';
const StyledScrollbarArea = styled.div.withConfig({ displayName: "StyledScrollbarArea", componentId: "sc-13nqjar" }) `height:${({ theme }) => theme.size.static500};position:relative;display:flex;justify-content:center;flex-flow:column;&:before{content:'';display:block;width:100%;height:2px;background:${({ theme }) => theme.colors.border.default};border-radius:2px;}`;
const StyledScrollbar = styled.div.attrs(({ x, slides }) => ({
    style: {
        '--progress': `${x}px`,
        '--slides': slides,
    },
})).withConfig({ displayName: "StyledScrollbar", componentId: "sc-13xb9w6" }) `position:absolute;${(props) => (props.theme.direction === 'rtl' ? 'right' : 'left')}:0;transform:${(props) => props.theme.direction === 'rtl'
    ? `translateX(calc(-1 * var(--progress)))`
    : `translateX(var(--progress))`};height:100%;width:33%;cursor:grab;user-select:none;display:flex;flex-flow:column;justify-content:center;${(props) => !props.isDragging &&
    `
		transition: transform 150ms ease-out;
	`} &:before{content:'';display:block;height:3px;background:${({ theme }) => theme.colors.controllers.off.default};border-radius:3px;transition:background-color 150ms;}&:focus{outline:none;}&:active{cursor:grabbing;}&:hover{&:before{background:${({ theme }) => theme.colors.controllers.off.hover};}}`;
const activeSlideIndex = {};
const isDragging = {};
const TIMEOUT_TO_ENABLE_SNAPPING = 500;
const getActualScrollPercentage = (scrollbarPosition, maxScrollbarPosition) => {
    const value = scrollbarPosition / (maxScrollbarPosition / 100);
    // at times the  calculation above returns NaN when both operands evaluate to 0
    if (value <= 0 || Number.isNaN(value)) {
        return 0;
    }
    if (value > 100) {
        return 100;
    }
    return value;
};
export const NavigationScrollbar = () => {
    const { index, length, slidesPerPage, slidesRef, id: galleryID, triggerChange, disableScrollSnap, } = React.useContext(GalleryContext);
    const theme = useTheme();
    const scrollbarAreaRef = React.useRef(null);
    const scrollbarRef = React.useRef(null);
    const [scrollbarAreaWidth, setScrollbarAreaWidth] = React.useState(0);
    const [scrollbarWidth, setScrollbarWidth] = React.useState(0);
    const [scrollbarPosition, setScrollbarPosition] = React.useState(0);
    const maxScrollbarPosition = React.useMemo(() => scrollbarAreaWidth - scrollbarWidth, [scrollbarAreaWidth, scrollbarWidth]);
    activeSlideIndex[galleryID] = index;
    const handleScrollbarPosition = React.useCallback((value) => {
        if (value <= 0) {
            return 0;
        }
        if (value > maxScrollbarPosition) {
            return maxScrollbarPosition;
        }
        return Math.floor(value);
    }, [maxScrollbarPosition]);
    const handleScrollbarPositionByGalleryPosition = React.useCallback(() => {
        const scrollbarArea = scrollbarAreaWidth - scrollbarWidth;
        const newPositionPercentage = activeSlideIndex[galleryID] / ((length - slidesPerPage) / 100) / 100;
        const newPosition = newPositionPercentage * scrollbarArea;
        return Math.round(newPosition);
    }, [galleryID, length, scrollbarAreaWidth, scrollbarWidth, slidesPerPage]);
    const setScrollbarSize = React.useCallback(() => {
        if (!scrollbarAreaRef ||
            !scrollbarAreaRef.current ||
            !scrollbarRef ||
            !scrollbarRef.current) {
            return;
        }
        setScrollbarAreaWidth(scrollbarAreaRef.current.clientWidth || 0);
        setScrollbarWidth(scrollbarRef.current.clientWidth || 0);
    }, []);
    // biome-ignore lint/correctness/useExhaustiveDependencies: we also want to set the scrollbar position when any of the other values in the dependency array change
    React.useEffect(() => {
        !isDragging[galleryID] &&
            setScrollbarPosition(handleScrollbarPositionByGalleryPosition());
    }, [
        index,
        length,
        scrollbarAreaWidth,
        scrollbarWidth,
        handleScrollbarPositionByGalleryPosition,
        galleryID,
    ]);
    React.useEffect(() => {
        if (!scrollbarRef ||
            !scrollbarRef.current ||
            !slidesRef ||
            !slidesRef.current) {
            return;
        }
        let mouseDownClientX = 0;
        const slideWidth = slidesRef.current.children[0].clientWidth;
        const slidePaddingRight = parseFloat(getComputedStyle(slidesRef.current.children[0]).getPropertyValue(`padding-${theme.direction === 'rtl' ? 'left' : 'right'}`));
        const totalSlideMargin = parseFloat(getComputedStyle(slidesRef.current.children[0]).getPropertyValue(`margin-${theme.direction === 'rtl' ? 'right' : 'left'}`)) * 2;
        setScrollbarSize();
        const handleDragging = (clientX) => {
            if (!slidesRef || !slidesRef.current) {
                return;
            }
            const deltaX = clientX - mouseDownClientX;
            const newScrollbarPosition = theme.direction === 'rtl'
                ? scrollbarPosition - deltaX
                : scrollbarPosition + deltaX;
            setScrollbarPosition(handleScrollbarPosition(newScrollbarPosition));
            const positionPercentage = getActualScrollPercentage(newScrollbarPosition, maxScrollbarPosition);
            const scrollTo = (positionPercentage / 100) *
                (slidesRef.current.scrollWidth -
                    slideWidth * slidesPerPage -
                    totalSlideMargin +
                    slidePaddingRight);
            slidesRef.current.scrollTo({
                left: Math.floor(theme.direction === 'rtl' ? scrollTo * -1 : scrollTo),
                behavior: 'auto',
            });
        };
        const handleDragEnd = () => {
            triggerChange(activeSlideIndex[galleryID]);
            setTimeout(() => {
                !isDragging[galleryID] && disableScrollSnap(false);
            }, TIMEOUT_TO_ENABLE_SNAPPING);
            setScrollbarPosition(handleScrollbarPositionByGalleryPosition());
        };
        const handleMouseDown = (event) => {
            isDragging[galleryID] = true;
            disableScrollSnap();
            mouseDownClientX = event.clientX;
            document.addEventListener('mousemove', handleMouseDrag);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'grabbing';
            document.body.style.userSelect = 'none';
            document.body.style.webkitUserSelect = 'none'; // still needed for Safari!
        };
        const handleMouseDrag = (event) => {
            handleDragging(event.clientX);
        };
        const handleMouseUp = () => {
            isDragging[galleryID] = false;
            document.removeEventListener('mousemove', handleMouseDrag);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'auto';
            handleDragEnd();
        };
        const handleTouchStart = (event) => {
            isDragging[galleryID] = true;
            disableScrollSnap();
            mouseDownClientX = event.touches[0].clientX;
            document.addEventListener('touchmove', handleTouchMove);
            document.addEventListener('touchend', handleTouchEnd);
        };
        const handleTouchMove = (event) => {
            handleDragging(event.touches[0].clientX);
        };
        const handleTouchEnd = () => {
            isDragging[galleryID] = false;
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
            handleDragEnd();
        };
        scrollbarRef.current.addEventListener('mousedown', handleMouseDown);
        scrollbarRef.current.addEventListener('touchstart', handleTouchStart);
        const scrollbarElement = scrollbarRef.current;
        return () => {
            scrollbarElement.removeEventListener('mousedown', handleMouseDown);
            scrollbarElement.removeEventListener('touchstart', handleTouchStart);
        };
    }, [
        slidesPerPage,
        slidesRef,
        scrollbarPosition,
        maxScrollbarPosition,
        disableScrollSnap,
        handleScrollbarPosition,
        handleScrollbarPositionByGalleryPosition,
        triggerChange,
        setScrollbarSize,
        theme.direction,
        galleryID,
    ]);
    const handleResize = React.useCallback(() => {
        setScrollbarSize();
    }, [setScrollbarSize]);
    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [handleResize]);
    const handleKeyDown = React.useCallback((event) => {
        var _a, _b;
        let newIndex;
        switch (event.key) {
            case 'ArrowLeft':
                newIndex = Math.max(index + ((_a = theme.direction) !== null && _a !== void 0 ? _a : 'ltr') === 'rtl' ? 1 : -1, 0);
                break;
            case 'ArrowRight':
                newIndex = Math.max(index + ((_b = theme.direction) !== null && _b !== void 0 ? _b : 'ltr') === 'rtl' ? -1 : 1, 0);
                break;
            case 'Home':
                newIndex = 0;
                break;
            case 'End':
                newIndex = length - 1;
                break;
            default:
                newIndex = null;
        }
        if (newIndex !== null) {
            event.stopPropagation();
            event.preventDefault();
            triggerChange(newIndex);
        }
    }, [index, length, theme.direction, triggerChange]);
    const handleScrollAreaClick = React.useCallback((event) => {
        if (!scrollbarAreaRef || !scrollbarAreaRef.current) {
            return;
        }
        const scrollbarAreaOffset = scrollbarAreaRef.current.getBoundingClientRect().left || 0;
        const deltaX = event.clientX - scrollbarAreaOffset;
        const newScrollbarPosition = theme.direction === 'rtl' ? scrollbarAreaWidth - deltaX : deltaX;
        const positionPercentage = getActualScrollPercentage(newScrollbarPosition, maxScrollbarPosition);
        const slideToItem = ((length - 1) / 100) * positionPercentage;
        triggerChange(slideToItem);
    }, [
        length,
        maxScrollbarPosition,
        scrollbarAreaWidth,
        theme.direction,
        triggerChange,
    ]);
    const handleScrollbarClick = React.useCallback((event) => {
        event.stopPropagation();
    }, []);
    const ariaScrolledPercentage = React.useMemo(() => Math.round(getActualScrollPercentage(scrollbarPosition, maxScrollbarPosition)), [scrollbarPosition, maxScrollbarPosition]);
    if (length <= slidesPerPage) {
        return null;
    }
    return (React.createElement(StyledScrollbarArea, { ref: scrollbarAreaRef, onClick: handleScrollAreaClick },
        React.createElement(StyledScrollbar, { ref: scrollbarRef, x: scrollbarPosition, isDragging: isDragging[galleryID], slides: length, tabIndex: 0, onKeyDown: handleKeyDown, onClick: handleScrollbarClick, role: "scrollbar", "aria-controls": galleryID, "aria-labelledby": galleryID, "aria-orientation": "horizontal", "aria-valuenow": ariaScrolledPercentage, "aria-valuemin": 0, "aria-valuemax": 100 })));
};
